import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { OwaDate } from 'owa-datetime';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import CalendarSurfaceStore from '../store/schema/CalendarSurfaceStore';
import { ColumnProvider } from '../interfaces/ColumnProvider';
import { getColumn } from '../logic/getColumn';
import { SurfaceItem } from '../interfaces/SurfaceItem';
import { CalendarSurfaceItemsConfig } from '../interfaces/CalendarItemsConfig';
import { SurfaceDragDropConfig } from '../store/schema/SurfaceDragDropConfig';
import getOverflowIndicatorId from '../utils/getOverflowIndicatorId';
import { wrapOverflowIndicatorToGridItem } from '../surfaceItems/GridItem';
import { LayoutPositionDetails } from '../interfaces/LayoutPositionDetails';
import { getHorizontalLayout } from '../logic/getHorizontalLayout';
import convertAllDayLayoutPositionToStyles from '../utils/convertAllDayLayoutPositionToStyles';
import { isItemMultiDayVisually } from 'owa-calendar-events-visual-date-range';

export interface OverflowIndicator {
    layout: LayoutPositionDetails;
    item: SurfaceItem;
}

export interface GenerateOverflowItemsTransformProps {
    dragDropConfig?: SurfaceDragDropConfig;
    getItemConfig: (model: CalendarEvent) => CalendarSurfaceItemsConfig | null;
}

export function generateOverflowItems(
    store: CalendarSurfaceStore,
    config: GenerateOverflowItemsTransformProps,
    hiddenItems: SurfaceItem[],
    transformItem: (item: SurfaceItem, rowIndex: number) => JSX.Element,
    columnProvider: ColumnProvider,
    week: number,
    maxRows: number,
    rowHeight: number,
    weekHeight: number,
    weekPadding: number,
    disableOverflowOptimization?: boolean
): JSX.Element[] {
    const result: JSX.Element[] = [];

    // if item spans multiple days, then split it so that it can be processed per day
    const hiddenItemsToProcess: SurfaceItem[] = [];
    hiddenItems.forEach(item => {
        const intervals = columnProvider.getColumnIntervals(item.model);
        intervals.forEach(interval => {
            hiddenItemsToProcess.push({ ...interval, ...{ model: item.model, key: item.key } });
        });
    });

    // attempt to optimize the overflow row if feature is enabled
    if (!disableOverflowOptimization) {
        // create a mapping of overflowId to item(s) to aid processing
        const overflowIdToHiddenItemsMap: Map<string, SurfaceItem[]> = groupHiddenItemsByOverflowId(
            store,
            hiddenItemsToProcess,
            columnProvider
        );

        // if any overflowId item group contains exactly one non-multiday item, remove it from hidden items and push it into overflow row
        const overflowRowItems: SurfaceItem[] = extractOverflowRowItems(
            overflowIdToHiddenItemsMap,
            hiddenItemsToProcess,
            hiddenItems
        );

        // if there are any items moved into the overflow row, then generate visible week items for that row
        if (overflowRowItems.length > 0) {
            // fill in placeholder/empty rows for all rows already handled (up to maxRows) so that final new row is positioned correctly
            const weekVisibleItems: SurfaceItem[][] = [];
            for (let i = 0; i < maxRows; i++) {
                weekVisibleItems.push([]);
            }
            weekVisibleItems.push(overflowRowItems);
            result.push(...generateVisibleWeekItems(weekVisibleItems, transformItem));
        }
    }

    // create overflow indicators for the remaining hidden items
    const overflowIndicatorList: OverflowIndicator[] = createOverflowIndicatorList(
        store,
        hiddenItemsToProcess,
        columnProvider,
        week,
        maxRows,
        rowHeight,
        weekHeight,
        weekPadding
    );

    // create an overflow grid item for each overflow indicator
    overflowIndicatorList.forEach(overflowIndicator => {
        const itemConfig = config.getItemConfig?.(overflowIndicator.item.model);
        result.push(
            wrapOverflowIndicatorToGridItem(
                {
                    ...convertAllDayLayoutPositionToStyles(overflowIndicator.layout),
                    position: 'absolute',
                },
                {
                    height: rowHeight,
                    width: '30px',
                    margin: 'auto',
                    position: 'relative',
                },
                overflowIndicator.item.key,
                itemConfig,
                overflowIndicator.item.model,
                itemConfig && itemConfig.template(store, overflowIndicator.item.model),
                null /* item index */,
                overflowIndicator.item.key /* tab Id */
            )
        );
    });

    return result;
}

function generateVisibleWeekItems(
    weekVisibleItems: SurfaceItem[][],
    transformItem: (item: SurfaceItem, rowIndex: number) => JSX.Element
): JSX.Element[] {
    let result: JSX.Element[] = [];

    weekVisibleItems.forEach((rowItems, rowIndex) => {
        rowItems.forEach(item => {
            result.push(transformItem(item, rowIndex));
        });
    });

    return result;
}

/** If any overflowId item group contains exactly one non-multiday item, remove it from hidden items and push it into overflow row
 * If the a multiday item is the last item in every group it appears it, remove it from the hidden items and push the whole thing into the overflow row
 */
function extractOverflowRowItems(
    overflowIdToHiddenItemsMap: Map<string, SurfaceItem[]>,
    hiddenItemsToProcess: SurfaceItem[],
    untransformedHiddenItems: SurfaceItem[]
): SurfaceItem[] {
    const overflowRowItems: SurfaceItem[] = [];
    const columnItemGroups: SurfaceItem[][] = [];

    // create 2D array of SurfaceItem[group][items]
    overflowIdToHiddenItemsMap.forEach(itemGroup => columnItemGroups.push(itemGroup));

    const occurrenceCount: Map<string, number> = new Map<string, number>();
    columnItemGroups.forEach(itemGroup => {
        if (itemGroup.length === 1 && !isItemMultiDayVisually(itemGroup[0].model)) {
            hiddenItemsToProcess.splice(hiddenItemsToProcess.indexOf(itemGroup[0]), 1);
            overflowRowItems.push(itemGroup[0]);
        } else if (itemGroup.length === 1) {
            // multiday item, add to list
            const item = itemGroup[0];
            const currentCount = occurrenceCount.get(item.model.ItemId.Id);
            occurrenceCount.set(
                item.model.ItemId.Id,
                currentCount === undefined ? 1 : currentCount + 1
            );
        }
    });

    // check through the multiday items occurrenceCount and if the count == number of columns that item appears in,
    // means the item is the last item in all of it's columns and should be added to overflowRowItems and removed from
    // hiddenItemsToProcess
    occurrenceCount.forEach((count, itemId) => {
        // see how many days this item occurs in
        let totalColumnCount = 0;
        for (let i = 0; i < columnItemGroups.length; i++) {
            if (
                columnItemGroups[i].filter(columnItem => columnItem.model.ItemId.Id === itemId)
                    .length > 0
            ) {
                totalColumnCount++;
            }
        }
        if (totalColumnCount === count) {
            // item is the last in the column for every day it occurs in, so pull it out
            removeItemCopiesFromHiddenItems(itemId, hiddenItemsToProcess);

            // push the full item (not the partial item that's been broken apart) back into overflowRowItems
            const untransformedItem = untransformedHiddenItems.filter(
                hiddenItem => itemId === hiddenItem.model.ItemId.Id
            )[0];
            overflowRowItems.push(untransformedItem);
        }
    });

    return overflowRowItems;
}

/**
 * Removes all occurrences of items with the given itemId from the hiddenItemsToProcess array
 * @param itemId
 * @param hiddenItemsToProcess
 */
function removeItemCopiesFromHiddenItems(itemId: string, hiddenItemsToProcess: SurfaceItem[]) {
    // item is the last in the column for every day it occurs in, so pull it out
    let indexToRemove = hiddenItemsToProcess.indexOf(
        hiddenItemsToProcess.filter(hiddenItem => hiddenItem.model.ItemId.Id === itemId)[0]
    );
    while (indexToRemove >= 0) {
        hiddenItemsToProcess.splice(indexToRemove, 1);
        indexToRemove = hiddenItemsToProcess.indexOf(
            hiddenItemsToProcess.filter(hiddenItem => hiddenItem.model.ItemId.Id === itemId)[0]
        );
    }
}

/** Create a mapping of overflowId to hidden item(s) */
function groupHiddenItemsByOverflowId(
    store: CalendarSurfaceStore,
    hiddenItemsToProcess: SurfaceItem[],
    columnProvider: ColumnProvider
): Map<string, SurfaceItem[]> {
    const overflowIdToHiddenItemsMap = new Map<string, SurfaceItem[]>();
    hiddenItemsToProcess.forEach(item => {
        const cellDate: OwaDate = columnProvider.getColumnDate(
            getColumn(item.start, store.dayLength)
        );
        const overflowId = getOverflowIndicatorId(cellDate);
        if (!overflowIdToHiddenItemsMap.has(overflowId)) {
            overflowIdToHiddenItemsMap.set(overflowId, []);
        }
        const overflowIdItems = overflowIdToHiddenItemsMap.get(overflowId);
        overflowIdItems.push(item);
        overflowIdToHiddenItemsMap.set(overflowId, overflowIdItems);
    });
    return overflowIdToHiddenItemsMap;
}

/** Create overflow indicators for the remaining hidden items */
function createOverflowIndicatorList(
    store: CalendarSurfaceStore,
    hiddenItemsToProcess: SurfaceItem[],
    columnProvider: ColumnProvider,
    week: number,
    maxRows: number,
    rowHeight: number,
    weekHeight: number,
    weekPadding: number
): OverflowIndicator[] {
    const overflowIndicatorList: OverflowIndicator[] = [];

    hiddenItemsToProcess.forEach(item => {
        const layout = calculateOverflowIndicatorLayout(
            item,
            columnProvider,
            weekPadding,
            week,
            maxRows,
            store.dayLength,
            store.userOptions.isRTL,
            rowHeight,
            weekHeight
        );

        const cellDate: OwaDate = columnProvider.getColumnDate(
            getColumn(item.start, store.dayLength)
        );
        const overflowId = getOverflowIndicatorId(cellDate);

        // checking if existing overflow indicator is found at same cell date
        const existingOverflowIndicatorFound: boolean =
            null !=
            findInArray(overflowIndicatorList, indicator => {
                return indicator.item.key == overflowId;
            });

        // if not found, then add a new overflow indicator for this cell date
        if (!existingOverflowIndicatorFound) {
            const id = overflowId;
            const newOverflowIndicatorItem: SurfaceItem = {
                key: id,
                start: NaN,
                end: NaN,
                model: {
                    ItemId: { Id: id },
                    Start: cellDate,
                    End: cellDate,
                } as CalendarEvent,
            };
            const newOverflowIndicator: OverflowIndicator = {
                layout: layout,
                item: newOverflowIndicatorItem,
            };
            overflowIndicatorList.push(newOverflowIndicator);
        }
    });

    return overflowIndicatorList;
}

function calculateOverflowIndicatorLayout(
    item: SurfaceItem,
    columnProvider: ColumnProvider,
    weekPadding: number,
    week: number,
    rowIndex: number,
    columnLength: number,
    isRTL: boolean,
    itemHeight: number,
    weekHeight: number
) {
    let horizontalLayout = getHorizontalLayout(item, columnProvider, columnLength, isRTL);
    let center = (horizontalLayout.left + 1 - horizontalLayout.right) / 2;
    return {
        left: center,
        transform: 'translateX(-50%)',
        top: weekHeight * week + rowIndex * itemHeight + weekPadding,
        height: itemHeight,
        zIndex: 2,
    };
}
